import * as React from 'react'
import { DateRange } from 'react-day-picker'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { SetStateAction } from 'react'
import { useUserStore } from '~/app/_store/user-store'
import { useStorage } from '~/hooks/use-storage'

type FiltersContextType = {
  members: string[]
  range: DateRange | undefined
}

export const FiltersContext = React.createContext<FiltersContextType>({
  members: [],
  range: undefined,
})

export const FiltersProvider = ({
  children,
  members,
  range,
}: {
  children: React.ReactNode
  members: string[]
  range: DateRange | undefined
}) => {
  return (
    <FiltersContext.Provider value={{ members, range }}>
      {children}
    </FiltersContext.Provider>
  )
}

export const useFilters = () => {
  const userId = useUserStore((s) => s.optimisticUser?.id)
  const replicache = useRepoReplicache().data?.replicache
  const [storageFilters, setStorageFilters] = useStorage(
    'localStorage',
    (z) =>
      z.object({
        members: z.array(z.string()),
        range: z
          .object({
            from: z.string(),
            to: z.string(),
          })
          .nullable(),
      }),
    'commits-filters',
    { initialValue: { members: [], range: null } }
  )

  const members = useUserPreferences((s) => s.commitsFilters?.members ?? [])
  const setMembers = React.useCallback(
    (v: SetStateAction<string[]>) => {
      if (typeof v === 'function') {
        v = v(members ?? [])
      }

      if (userId && replicache) {
        replicache.mutate.setCommitsFilters({
          members: v,
          filterType: 'commitsFilters',
          userId,
        })
      } else {
        setStorageFilters((prev) => ({
          members: v,
          range: prev?.range ?? null,
        }))
      }
    },
    [userId, replicache, members, setStorageFilters]
  )

  const range = useUserPreferences((s) =>
    s.commitsFilters?.range
      ? {
          from: new Date(s.commitsFilters.range.from),
          to: new Date(s.commitsFilters.range.to),
        }
      : undefined
  )

  const setRange = React.useCallback(
    (v: SetStateAction<DateRange | undefined>) => {
      if (typeof v === 'function') {
        v = v(range)
      }

      if (userId && replicache) {
        replicache.mutate.setCommitsFilters({
          range:
            v?.from && v.to
              ? {
                  from: v.from.toISOString(),
                  to: v.to.toISOString(),
                }
              : null,
          filterType: 'commitsFilters',
          userId,
        })
      } else {
        setStorageFilters((prev) => ({
          members: prev?.members ?? [],
          range:
            v?.from && v.to
              ? {
                  from: v.from.toISOString(),
                  to: v.to.toISOString(),
                }
              : null,
        }))
      }
    },
    [userId, replicache, range, setStorageFilters]
  )

  // Use storage filters as fallback when not logged in or no replicache
  const effectiveMembers = React.useMemo(
    () => (userId && replicache ? members : storageFilters.members),
    [userId, replicache, members, storageFilters.members]
  )

  const effectiveRange = React.useMemo(
    () =>
      userId && replicache
        ? range
        : storageFilters.range
        ? {
            from: new Date(storageFilters.range.from),
            to: new Date(storageFilters.range.to),
          }
        : undefined,
    [userId, replicache, range, storageFilters.range]
  )

  return React.useMemo(
    () => ({
      range: effectiveRange,
      setRange,
      members: effectiveMembers,
      setMembers,
    }),
    [effectiveRange, setRange, effectiveMembers, setMembers]
  )
}
